import { enableProdMode, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import { environment } from '@env/environment';
import { provideRouter, RouterOutlet, withComponentInputBinding, withRouterConfig } from '@angular/router';
import { provideViewportPreloading, provideDialogEnvironment, provideKDSTheme, provideToast } from '@kaseya-design-components';
import { routes } from '@app/routes';
import { withNgxsResetPlugin } from 'ngxs-reset-plugin';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { RefreshTokenInterceptor } from '@core/authorization/interceptors/refresh-token.interceptor';
import { TokenInterceptor } from '@core/authorization/interceptors/token.interceptor';
import { RequestForbiddenInterceptor } from '@core/errors/interceptors/request-forbidden.interceptor';
import { detectIpSwitchInterceptor } from '@core/errors/interceptors/detect-ip-switch/detect-ip-switch.interceptor';
import { errorsInterceptor } from '@core/errors/interceptors/errors/errors.interceptor';
import { RetryWhenOnlineInterceptor } from '@core/errors/interceptors/retry-when-online.interceptor';

import { provideEnvironmentNgxMask } from 'ngx-mask';
import { provideUserInfoEnvironment } from '@core/user-info/models/user-info.environment';
import { bootstrapApplication, BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { FormsModule } from '@angular/forms';
import { provideStore } from '@ngxs/store';
import { moduleTrialRequestsState } from '@core/module-trial-requests/store';
import { inkyBannerSettingsState } from '@core/inky-banner-settings/store';
import { loginStatusState } from '@core/login-status/store';
import { partnerProgramState } from '@layout/partner-program/store';
import { kaseya365UpgradeState as kaseya365State } from '@layout/kaseya-365/store';
import { launcherState } from '@layout/launcher/store';
import { notificationsState } from '@layout/notifications/store';
import { moduleConfigurationsStates } from '@layout/module-management/store';
import { featuresState } from '@core/features/store';
import { accountTeamState } from '@layout/account-team/store';
import { applicationVersionState } from '@core/application-version/store';
import { firstTimeOnboardingCompletedStates } from '@layout/first-time-onboarding/store';
import { k365SubscriptionsState } from '@layout/k365-subscriptions/store';
import { k365SetupGuidesAccessState } from '@layout/k365-setup-guides-access/store';
import { partnerProgramActiveState } from '@layout/partner-program-active/store';
import { paymentManagementActiveState } from '@layout/payment-management-active/store';
import { userInfoState } from '@core/user-info/store';
import { permissionsState } from '@core/permissions/store';
import { navigationState } from '@core/navigation/store';
import { menuSettingsState } from '@layout/left-panel/store';
import { locationsState } from '@core/locations/store';
import { rolesState } from '@core/roles/store';
import { videoTutorialsState } from '@layout/tutorials/store';
import { withNgxsReduxDevtoolsPlugin } from '@ngxs/devtools-plugin';
import { AppComponent } from '@app/app.component';
import { tenantsState } from '@core/sso/store';
import { walkMeState } from '@core/walk-me/store';
import { featuresSetupState } from '@layout/features-setup/store';
import { deepLinkState } from '@core/deep-link/store';
import { withNgxsStoragePlugin } from '@ngxs/storage-plugin';
import { DeepLinkState } from '@core/deep-link/store/states/deep-link.state';
import { medalliaState } from '@core/medallia/store';
import { ssoStatusState } from '@layout/sso-status/store';
import { ssoMappingRulesBannerState } from '@layout/sso-mapping-rules-banner/store';
import { assignedRenewalContactsState } from '@layout/renewal-contacts/store';
import { provideTranslations } from '@core/translation/models/translations.provider';
import { provideAda } from '@core/ada/providers/ada.provider';
import { adaState } from '@core/ada/store';
import { provideMedallia } from '@core/medallia/providers/medallia.provider';
import { themeState } from '@core/theme/store';
import { provideTheme } from '@core/theme/providers/theme.provider';
import { LocalStorageKey } from '@core/storage/models/local-storage-key.enum';
import { provideDateDefaults } from '@core/datetime/providers/date-defaults.provider';
import {
  provideRemoteIntegrationSharedEvents,
  provideRemoteIntegrationSharedMethods,
  provideRemoteIntegrationSharedMountProps,
} from '@remote-integration';
import { remoteIntegrationShellMethodsFactory } from '@core/remote-integration/providers/remote-integration-shell-methods.provider';
import { remoteIntegrationShellEventsFactory } from '@core/remote-integration/providers/remote-integration-shell-events.provider';

if (environment.production) {
  enableProdMode();
}

async function prepareApp(): Promise<ServiceWorkerRegistration | undefined> {
  if (environment.envName === 'localhost') {
    const { worker } = await import('./app/shared/utils/browser-mocks');
    return worker.start({ onUnhandledRequest: 'bypass' });
  }

  return Promise.resolve(undefined);
}

prepareApp().then(() => {
  bootstrapApplication(AppComponent, {
    providers: [
      provideZoneChangeDetection(),
      importProvidersFrom(BrowserModule, FormsModule, RouterOutlet),
      provideRouter(
        routes,
        withComponentInputBinding(),
        withRouterConfig({
          onSameUrlNavigation: 'reload',
        })
      ),
      provideViewportPreloading(),
      provideStore(
        [
          ...ssoStatusState,
          ...ssoMappingRulesBannerState,
          ...assignedRenewalContactsState,
          ...partnerProgramState,
          ...kaseya365State,
          ...launcherState,
          ...notificationsState,
          ...moduleConfigurationsStates,
          ...featuresState,
          ...accountTeamState,
          ...applicationVersionState,
          ...tenantsState,
          ...firstTimeOnboardingCompletedStates,
          ...k365SubscriptionsState,
          ...k365SetupGuidesAccessState,
          ...partnerProgramActiveState,
          ...paymentManagementActiveState,
          ...userInfoState,
          ...permissionsState,
          ...navigationState,
          ...menuSettingsState,
          ...locationsState,
          ...rolesState,
          ...videoTutorialsState,
          ...walkMeState,
          ...featuresSetupState,
          ...deepLinkState,
          ...medalliaState,
          ...adaState,
          ...themeState,
          ...moduleTrialRequestsState,
          ...inkyBannerSettingsState,
          ...loginStatusState,
        ],
        {
          developmentMode: !environment.production,
        }
      ),
      withNgxsStoragePlugin({
        keys: [DeepLinkState, `${LocalStorageKey.Theme}.preference`],
      }),
      withNgxsResetPlugin(),
      withNgxsReduxDevtoolsPlugin({
        disabled: environment.envName === 'prod',
      }),
      {
        provide: HTTP_INTERCEPTORS,
        useClass: RefreshTokenInterceptor,
        multi: true,
      },
      {
        provide: HTTP_INTERCEPTORS,
        useClass: TokenInterceptor,
        multi: true,
      },
      {
        provide: HTTP_INTERCEPTORS,
        useClass: RequestForbiddenInterceptor,
        multi: true,
      },
      {
        provide: HTTP_INTERCEPTORS,
        useClass: RetryWhenOnlineInterceptor,
        multi: true,
      },
      provideToast(),
      provideEnvironmentNgxMask(),
      provideHttpClient(withInterceptorsFromDi(), withInterceptors([detectIpSwitchInterceptor, errorsInterceptor])),
      provideDialogEnvironment(),
      provideUserInfoEnvironment(),
      provideTranslations(),
      provideAda(),
      provideMedallia(),
      provideAnimations(),
      provideKDSTheme(),
      provideTheme(),
      provideDateDefaults(),
      provideRemoteIntegrationSharedEvents(remoteIntegrationShellEventsFactory),
      provideRemoteIntegrationSharedMountProps(() => ({ baseURL: environment.portalApiUrl })),
      provideRemoteIntegrationSharedMethods(remoteIntegrationShellMethodsFactory),
    ],
  }).catch(err => console.error(err));
});
